import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

/**
 * Session gate for the owner console (`/owner/*`).
 *
 * This is an *authentication* gate, not a role gate — deliberately.
 *
 *   - `lib/seo/routePolicy.ts:174` classifies `/owner` as `private`, the same
 *     tier as `/seller` and `/dashboard`, not as an admin surface.
 *   - `useUserRoles` aliases the `owner` nav role to `business`, and the nav
 *     entry sits in the signed-in dropdown (`NavbarAuthSection.tsx:385`).
 *   - Ownership is per-row, not per-role: `business_listings.owner_id` and
 *     `edu_providers.owner_id` are what lib/ownerOps.ts filters on, and every
 *     one of those queries is scoped to `auth.uid()`. RLS is the data boundary.
 *
 * Gating this on `has_role(admin)` would lock out the business owners the
 * console exists for, and would still not be the boundary that protects data.
 *
 * What was actually missing was the session check. Without it a signed-out
 * visitor got the full dashboard shell: `fetchOwnedEntities()` returns `[]`
 * when `requireUser()` finds nobody, and the pages render that empty list as
 * "you own nothing yet" — a failed read published as a fact, plus an
 * invitation to create a business that may already exist.
 *
 * We redirect rather than render an error screen because "no session" is a
 * known state with a known destination, not an unreadable one.
 */
export const useOwnerGuard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    // `replace` keeps the guarded URL out of history, so Back from /auth does
    // not bounce the user straight back into the redirect.
    if (!user) navigate("/auth", { replace: true });
  }, [user, loading, navigate]);

  return { ready: !loading && !!user, loading, user };
};
